import { useMantineTheme, Paper, Stack, Skeleton, DEFAULT_THEME } from '@mantine/core';
import { useClipboard, useMediaQuery } from '@mantine/hooks';
import { useSessionContext, useSupabaseClient } from '@supabase/auth-helpers-react';
import { motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState, useRef, useEffect, useCallback } from 'react';

import { SatoshiVariable } from '../pages/_app';
import {
  TIER_IMAGES_CDN,
  FALLBACK_IMAGE,
  EDGE_FUNCTIONS_URL,
  WEBSITE_DOMAIN,
  IS_DEV,
} from '../src/Constants';
import { exportAsBlob, invoke, exportAsImage, supabaseAnonClient, fillText } from '../src/Utils';
import { useStore } from '../stores/stores';
import { Database } from '../types/supabase';
import { TierData } from './GradientPicker/GradientPicker';
import { TemplateData, TierListData } from './TemplateResults';
import { TableImageData } from './TierListPage';
import ShareModal from './TierListPage/ShareModal';
import TierGrid, { TierItemData } from './TierListPage/TierGrid';
import TierListHeader from './TierListPage/TierListHeader';

export const getImage = async (
  tierItems: TierItemData[][],
  tiersData: TierData[],
  format: 'vertical' | 'horizontal' | 'square',
  darkMode: boolean,
  asBlob: boolean
): Promise<string | Blob | null> => {
  const canvas = document.createElement('canvas');

  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }

  const pageWidth = format === 'square' ? 1080 : format === 'vertical' ? 1080 : 1920;
  const pageHeight = format === 'square' ? 1080 : format === 'vertical' ? 1920 : 1080;

  canvas.width = pageWidth;
  canvas.height = pageHeight;

  context.fillStyle = darkMode ? DEFAULT_THEME.colors.dark[5] : DEFAULT_THEME.colors.gray[1];
  context.fillRect(0, 0, pageWidth, pageHeight);

  const displayItems = tierItems.slice(0, tierItems.length - 1);

  const found = false;
  let testBoxHeight = 100;
  const testPadding = 12.5;

  let delta = 16;
  let upwards = true;
  const minDelta = 0.001;

  let closest = Infinity;

  while (!found) {
    const boxesPerRow = Math.floor((pageWidth - testBoxHeight - testPadding) / testBoxHeight);
    const rowHeight = testBoxHeight + testPadding * 2;
    const rowHeights = displayItems.map((row, i) => {
      let rowItems = row.length;
      if (i === displayItems.length - 1) {
        rowItems += 1;
      }
      const height = Math.ceil(rowItems / boxesPerRow) * rowHeight;
      return Math.max(rowHeight, height);
    });

    const maxHeight = rowHeights.reduce((a, b) => a + b, 0);

    if (maxHeight === pageHeight) {
      break;
    }

    const dif = Math.abs(maxHeight - pageHeight);
    if (dif < closest) {
      closest = dif;
    }

    if (maxHeight < pageHeight) {
      if (!upwards) {
        if (delta < minDelta) {
          break;
        }
        delta /= 2;
        upwards = true;
      }
      testBoxHeight += delta;
    } else {
      if (upwards) {
        delta /= 2;
        upwards = false;
      }
      testBoxHeight -= delta;
    }
  }

  const boxesPerRow = Math.floor((pageWidth - testBoxHeight - testPadding) / testBoxHeight);
  const rowHeight = testBoxHeight + testPadding * 2;

  const rowHeights = displayItems.map((row, i) => {
    let rowItems2 = row.length;
    if (i === displayItems.length - 1) {
      rowItems2 += 1;
    }
    const height = Math.ceil(rowItems2 / boxesPerRow) * rowHeight;
    return Math.max(rowHeight, height);
  });

  const maxHeight = rowHeights.reduce((a, b) => a + b, 0);
  const offsetY = (pageHeight - maxHeight) / 2;

  rowHeights[0] += offsetY;
  rowHeights[rowHeights.length - 1] += offsetY;
  const boxHeight = testBoxHeight;
  const boxPadding = testPadding;

  const drawRow = (items: TierItemData[], index: number) => {
    const tierData = tiersData[index];
    const imagePromises = [];
    context.fillStyle = 'black';
    context.lineWidth = 3;
    const heightsAbove = rowHeights.slice(0, index).reduce((a, b) => a + b, 0);

    context.strokeRect(0, heightsAbove, pageWidth, rowHeights[index]);

    //Draw Tier Label

    context.fillStyle = tierData.backgroundColor ?? '#000000';
    context.fillRect(0, heightsAbove, boxHeight, rowHeights[index]);

    console.log(index);
    if (index === tiersData.length - 1) {
      console.log('brand');
      context.fillStyle = darkMode ? DEFAULT_THEME.colors.dark[8] : DEFAULT_THEME.colors.gray[3];
      const x = pageWidth - boxHeight - boxPadding;
      const y = heightsAbove - boxPadding + rowHeights[index] - boxHeight - offsetY;

      imagePromises.push(
        new Promise<void>((resolve) => {
          const heightOffset = index === 0 || index === displayItems.length - 1 ? offsetY / 2 : 0;
          const image = new Image(boxHeight, boxHeight);
          image.setAttribute('crossorigin', 'anonymous'); // works for me
          image.src = darkMode
            ? 'https://api.lomolist.com/storage/v1/object/public/uploads/Branding.png'
            : 'https://api.lomolist.com/storage/v1/object/public/uploads/Branding_White.png';
          image.addEventListener('load', (e) => {
            context.save();
            function roundedImage(
              ctx: CanvasRenderingContext2D,
              width: number,
              height: number,
              radius: number
            ) {
              ctx.beginPath();
              ctx.moveTo(x + radius, y);
              ctx.lineTo(x + width - radius, y);
              ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
              ctx.lineTo(x + width, y + height - radius);
              ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
              ctx.lineTo(x + radius, y + height);
              ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
              ctx.lineTo(x, y + radius);
              ctx.quadraticCurveTo(x, y, x + radius, y);
              ctx.closePath();
            }

            roundedImage(context, boxHeight, boxHeight, 6);
            context.clip();
            context.drawImage(image, x, y, boxHeight, boxHeight);

            context.restore();
            resolve();
          });

          image.addEventListener('error', (e) => {
            resolve();
          });
        })
      );
    }

    context.textAlign = 'center';
    // context.textBaseline = "middle";
    context.textBaseline = 'bottom';
    context.fillStyle = '#FFFFFF';

    fillText(context, tierData.name, {
      font: SatoshiVariable.style.fontFamily,
      rect: {
        x: boxHeight / 2,
        y: heightsAbove,
        width: boxHeight,
        height: rowHeights[index],
      },
    });

    for (let i = 0; i < items.length; i += 1) {
      // eslint-disable-next-line @typescript-eslint/no-loop-func
      imagePromises.push(
        new Promise<void>((resolve) => {
          const heightOffset = index === 0 || index === displayItems.length - 1 ? offsetY / 2 : 0;
          const image = new Image(boxHeight, boxHeight);
          image.setAttribute('crossorigin', 'anonymous'); // works for me
          image.addEventListener('load', (e) => {
            context.save();
            function roundedImage(
              ctx: CanvasRenderingContext2D,
              x: number,
              y: number,
              width: number,
              height: number,
              radius: number
            ) {
              ctx.beginPath();
              ctx.moveTo(x + radius, y);
              ctx.lineTo(x + width - radius, y);
              ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
              ctx.lineTo(x + width, y + height - radius);
              ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
              ctx.lineTo(x + radius, y + height);
              ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
              ctx.lineTo(x, y + radius);
              ctx.quadraticCurveTo(x, y, x + radius, y);
              ctx.closePath();
            }

            const x = boxHeight + boxPadding / 2 + boxHeight * (i % boxesPerRow);
            const y =
              heightsAbove + boxPadding + heightOffset + Math.floor(i / boxesPerRow) * rowHeight;

            roundedImage(context, x, y, boxHeight, boxHeight, 6);
            context.clip();
            context.drawImage(image, x, y, boxHeight, boxHeight);

            context.restore();
            resolve();
          });

          image.addEventListener('error', (e) => {
            resolve();
          });
          image.src = items[i].src;
        })
      );
    }
    return Promise.all(imagePromises);
  };

  const promises = displayItems.map((tierItem, index) => drawRow(tierItem, index));

  await Promise.all(promises);

  const finalUrl = await new Promise<string | Blob>((resolve) => {
    canvas.toBlob(
      (tBlob) => {
        const finalBlob = tBlob;
        if (finalBlob) {
          if (asBlob) {
            resolve(finalBlob);
          } else {
            const url = URL.createObjectURL(finalBlob);
            resolve(url);
          }
        }
      },
      'image/png',
      1.0
    );
  });

  return finalUrl;
};

export default function TemplateComponent({
  tierListData,
  saveData,
}: {
  tierListData: TemplateData;
  saveData: TierListData | null;
}) {
  const router = useRouter();
  const [loaded, setLoaded] = useState(false);

  //const [shareOpened, { open: openShare, close: closeShare }] = useDisclosure(false);
  const [shareOpened, setShareOpened] = useState('');
  const [shareImage, setShareImage] = useState<string | null>(null);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [sharerName, setSharerName] = useState<string | null>(null);
  const theme = useMantineTheme();
  const { session, isLoading } = useSessionContext();
  const tiersContainerRef = useRef<HTMLDivElement>(null);
  const supabase = useSupabaseClient<Database>();
  const [tierItems, setTierItems] = useState<TierItemData[][]>([]);
  const [tierSaveId, setTierSaveId] = useState<number>(-1);
  const [tierPublic, setTierPublic] = useState<boolean>(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [likedPost, setLikedPost] = useState(false);
  const { setLoginOpen } = useStore(null);
  // eslint-disable-next-line prefer-destructuring
  const images: TableImageData[] = (tierListData.data as any).images;
  const tiersData: TierData[] = (tierListData.data as any).tierData;
  const clipboard = useClipboard({ timeout: 500 });
  const url = `https://lomolist.com/${tierListData.id}/${tierListData.slug}`;
  const loginPendingRef = useRef('');

  const itemsRef = useRef<TierItemData[][]>([]);

  useEffect(() => {
    if (!itemsRef.current.length) {
      itemsRef.current = tierItems;
    }
  }, [tierItems]);

  const templateUpdateItems = (items: TierItemData[][]): void => {
    itemsRef.current = items;
  };

  const checkLikes = async () => {
    let tmpLikedPost = false;
    const likedResponse = await supabase
      .from('template_likes')
      .select('*')
      .eq('template_id', tierListData.id)
      .eq('user_id', session?.user.id)
      .single();

    if (likedResponse.data) {
      tmpLikedPost = true;
    }
    setLikedPost(tmpLikedPost);
  };

  useEffect(() => {
    // Always do navigations after the first render
    if (isLoading) {
      return;
    }

    const newItems: TierItemData[][] = [];

    tiersData.forEach(() => {
      newItems.push([]);
    });

    newItems.push(
      images.map((image) => ({
        id: image.id ?? image.path,
        src: `${TIER_IMAGES_CDN}/${image.path}`,
        name: image.name,
      }))
    );

    async function loadOldMap(oldMap: string[][]) {
      console.log('LOADING OLD PATH WTF', oldMap);
      const sortedImageIds: string[] = [];
      const cachedItems: TierItemData[][] = oldMap.map(
        (row: string[]) =>
          row
            .map((id) => {
              const match = images.find(
                (img: TableImageData) =>
                  (img.id === id && !sortedImageIds.includes(img.id)) ||
                  (img.path === id && !sortedImageIds.includes(img.path))
              );
              if (match) {
                if (sortedImageIds.includes(match.id ?? match.path)) {
                  console.log('DUPLICATE ID', match.id ?? match.path);
                  return null;
                }
                sortedImageIds.push(match.id ?? match.path);
              }
              return {
                id: match?.id ?? match?.path ?? '',
                src: match?.path ? `${TIER_IMAGES_CDN}/${match.path}` : FALLBACK_IMAGE,
                name: match?.name ?? '',
              };
            })
            .filter((imgData) => imgData?.id && imgData?.src) as TierItemData[]
      );

      console.log('CACHED ITEMS', cachedItems);
      images.forEach((img: TableImageData) => {
        if (!sortedImageIds.includes(img.path) && (!img.id || !sortedImageIds.includes(img.id))) {
          //Add new images to the bottom row
          cachedItems[cachedItems.length - 1].push({
            id: img.path,
            src: `${TIER_IMAGES_CDN}/${img.path}`,
            name: img.name,
          });
        }
      });

      console.log('CACHED ITEMS', cachedItems);

      setTierItems(cachedItems);
    }

    async function loadSharedTierData() {
      try {
        let oldMap: string[][] | null = null;
        console.log('loading shared tier data');
        const response = await supabase
          .from('tier_lists_save')
          .select('data')
          .eq('template_id', tierListData.id)
          .eq('save_id', saveData?.save_id)
          .single();

        if (response && response.data && (response.data.data as any)?.length) {
          oldMap = response.data.data as any;
          console.log('Loaded from DB');
        }

        if (!oldMap) {
          const savedData = localStorage.getItem(`TierData-${tierListData.id}`);
          if (savedData) {
            oldMap = JSON.parse(savedData);
            console.log('Loaded from Storage');
          }
        }

        if (oldMap) {
          loadOldMap(oldMap);
          return;
        }
        setTierItems(newItems);
      } catch (error) {
        setTierItems(newItems);
      } finally {
        setDataLoaded(true);
        if (saveData && saveData.save_id) {
          await supabase.rpc('on_tier_list_view', {
            id: saveData?.save_id,
          });
        }
      }
    }

    async function loadTierData() {
      try {
        let oldMap: string[][] | null = null;

        if (session?.user.id) {
          const response = await supabase
            .from('tier_lists_save')
            .select('data,save_id,public')
            .eq('template_id', tierListData.id)
            .eq('user_id', session?.user.id)
            .single();

          if (response && response.data && (response.data.data as any)?.length) {
            oldMap = response.data.data as any;
            const is_public = response.data.public;
            const { save_id } = response.data;
            setTierPublic(is_public);
            setTierSaveId(save_id);
            console.log('Loaded from DB', is_public, save_id);
          }
          await checkLikes();
        }

        if (!oldMap) {
          const savedData = localStorage.getItem(`TierData-${tierListData.id}`);
          if (savedData && savedData !== '[]') {
            oldMap = JSON.parse(savedData);
            console.log('Loaded from Storage');
          }
        }

        if (oldMap && oldMap.length) {
          loadOldMap(oldMap);
          return;
        }
        setTierItems(newItems);
      } catch (error) {
        setTierItems(newItems);
      } finally {
        setDataLoaded(true);
        if (!IS_DEV) {
          await supabase.rpc('on_template_view', {
            id: tierListData.id,
          });
        } else {
          console.log('Views not logged in dev');
        }
      }
    }

    if (!saveData) {
      loadTierData();
    } else {
      loadSharedTierData();
    }

    setLoaded(true);
  }, [isLoading]);

  const isMobile = useMediaQuery('(max-width: 60em)');

  const clickLikePost = async () => {
    if (!session?.user.id) {
      setLoginOpen(true);
      return;
    }

    if (likedPost) {
      await supabase
        .from('template_likes')
        .delete()
        .eq('template_id', tierListData.id)
        .eq('user_id', session?.user.id);
    } else {
      await supabase
        .from('template_likes')
        .insert({ template_id: tierListData.id, user_id: session?.user.id });
    }

    await checkLikes();
  };

  const removeSaveIDFromURL = () => {
    router.push('/[tierId]/[[...slug]]', `/${tierListData.id}/${tierListData.slug}`, {
      shallow: false,
    });
  };

  const onShareableLink = async (justUpdate = false, modalShare = false) => {
    if (!tierPublic && justUpdate) {
      console.log('Trying to update nonpublic tier, reject');
      return;
    }
    if (tierPublic && modalShare) {
      console.log('Modal share unneccessary');
      return;
    }
    if (tierSaveId === -1) {
      console.log('No Save ID, user not logged in', !justUpdate, 'NEXT', !modalOpen);
      if (!justUpdate && !modalOpen) {
        console.log('SHOULD WORK');
        setModalOpen(true);
      }
      return;
    }
    if (!tiersContainerRef.current) {
      console.log('No container ref');
      return;
    }
    const token = session?.access_token;
    if (token) {
      if (!justUpdate) {
        clipboard.copy(`${url}/list/${tierSaveId}`);
      }
      const formdata = new FormData();

      const imageBlob = (await getImage(
        itemsRef.current,
        tiersData,
        'square',
        theme.colorScheme === 'dark',
        true
      )) as Blob | null;

      if (!imageBlob) {
        return;
      }

      formdata.append('save_id', String(tierSaveId));
      formdata.append('thumbnail', imageBlob);
      formdata.append('parent_visibility', tierListData.visibility);
      const { data, error: shareImageError } = await invoke<
        Database['public']['Tables']['tier_lists_save']['Row']
      >('set-thumbnail', EDGE_FUNCTIONS_URL, token, {
        body: formdata,
      });

      if (shareImageError) {
        console.error('ERROR', shareImageError);
      } else {
        setTierPublic(true);
      }
    }
  };

  const onDataUpdated = useCallback(
    (new_save_id: number) => {
      setTierSaveId(new_save_id);
    },
    [setTierSaveId]
  );

  const onShare = useCallback(
    async (openShare = 'link') => {
      setShareImage(null);
      setShareOpened(openShare);
      if (tiersContainerRef.current) {
        const image = (await getImage(
          itemsRef.current,
          tiersData,
          isMobile ? 'vertical' : 'horizontal',
          theme.colorScheme === 'dark',
          false
        )) as string | null;
        setShareImage(image);
      }
    },
    [isMobile, theme.colorScheme, tiersData]
  );

  useEffect(() => {
    async function loadCreatorName() {
      const { data } = await supabaseAnonClient
        .from('profiles_public')
        .select('username')
        .eq('id', tierListData.creator_id)
        .single();
      if (data) {
        setCreatorName(data.username);
      }

      if (saveData) {
        const saveProfile = await supabaseAnonClient
          .from('profiles_public')
          .select('username')
          .eq('id', saveData?.user_id)
          .single();
        if (saveProfile.data) {
          setSharerName(saveProfile.data.username);
        }
      }
    }
    loadCreatorName();
  }, [tierListData]);

  useEffect(() => {
    if (loginPendingRef.current) {
      setShareOpened(loginPendingRef.current);
    }
  }, [session?.user.id]);

  const formattedName = tierListData.name;
  const formattedDescription = tierListData.description;
  const metaDescription = `Create your own ${formattedName} Tier List! ${formattedDescription}. Lomo is the best platform for creating and sharing Tier Lists!`;
  const thumbnailUrl = `${TIER_IMAGES_CDN}/${
    saveData ? saveData.thumbnail : tierListData.thumbnail
  }`;
  const metaTitle = `Create a ${tierListData.name} Tier List | Lomo`;
  const metaUrl = `${WEBSITE_DOMAIN}/${tierListData.id}/${tierListData.slug}`;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <Head>
        <title>{metaTitle}</title>
        {tierListData.category === 'NSFW' && <meta name="robots" content="noindex,nofollow" />}
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:image" content={thumbnailUrl} />
        <meta name="og:description" content={metaDescription} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={metaUrl} />
        <meta property="twitter:site" content="@SethSetse" />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:image" content={thumbnailUrl} />
        <meta property="og:site_name" content="LoMo" />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={metaUrl} />
      </Head>
      <Paper p="xs" maw={theme.breakpoints.xl} mx="auto">
        <ShareModal
          openedValue={shareOpened}
          tierListData={tierListData}
          session={session}
          onClose={(loginPending = '') => {
            if (loginPending) {
              loginPendingRef.current = loginPending;
            }
            setShareOpened('');
          }}
          shareImage={shareImage}
          save_id={tierSaveId}
          onShareList={onShareableLink}
        />
        <Stack>
          <TierListHeader
            thumbnail={tierListData.thumbnail}
            tierListData={tierListData}
            creatorName={creatorName ?? ''}
            isCreator={session?.user.id === tierListData.creator_id}
            isSharer={session?.user.id === saveData?.user_id}
            onShare={onShare}
            onShareableLink={onShareableLink}
            save_data={saveData}
            sharedUsername={sharerName ?? ''}
            clickLikedPost={clickLikePost}
            likedPost={likedPost}
          />
          <div>
            {loaded && (tierListData.data as any).tierData && tierItems.length ? (
              <TierGrid
                tiersData={tiersData}
                tierId={tierListData.id}
                tiersContainerRef={tiersContainerRef}
                dataLoaded={dataLoaded}
                savedTierItems={tierItems}
                onDataUpdated={onDataUpdated}
                isShared={!!saveData}
                removeSaveIDFromURL={removeSaveIDFromURL}
                onShareableLink={onShareableLink}
                onShare={onShare}
                sharedUserName={sharerName ?? ''}
                isMobile={isMobile}
                templateUpdateItems={templateUpdateItems}
              />
            ) : (
              <>
                <Skeleton h={635} />
                <Skeleton mt={75} h={120} />
              </>
            )}
          </div>
        </Stack>
      </Paper>
    </motion.div>
  );
}
